// IDV service (Flask). Same domain as rule-frontend in staging/production;
// set REACT_APP_IDV_API_URL in .env.local to point at a local backend.
function readEnvUrl() {
  try {
    return process.env.REACT_APP_IDV_API_URL;
  } catch {
    return undefined;
  }
}
// Default is same-origin (""), so /idv/... is served by whichever host the app
// is on — demo.idenza.ai, identity.idenza.ai, or any future name. nginx proxies
// /idv/ to the idv container on every one of them.
const API_URL = (readEnvUrl() || "").replace(/\/+$/, "");

// Where Veriff should send the user back: this app's own address + /verification/loading.
// Worked out from the current page, e.g. https://identity.idenza.ai/verification/loading
function returnUrl() {
  const path = window.location.pathname;
  const i = path.indexOf("/id-verification");
  const base = i >= 0 ? path.slice(0, i) : "";
  return `${window.location.origin}${base}/verification/loading`;
}

const STORAGE_KEY = "idv_session";

// Shown when the service can't be reached or returns no message of its own.
const SERVICE_ERROR =
  "Verification service is unavailable right now. Please try again later or contact support.";

export const SUCCESS_STATUSES = ["approved"];
export const FAILURE_STATUSES = ["declined", "resubmission_requested", "expired", "abandoned"];

// Creates the Veriff session and returns the Veriff URL to send the user to.
export async function startVerification({ firstName, lastName, email }) {
  const res = await fetch(`${API_URL}/idv/verification/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      returnUrl: returnUrl(),
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.veriffUrl) {
    // Show only what the service chose to expose — never raw provider errors.
    throw new Error(data.error || SERVICE_ERROR);
  }

  // Nothing is stored on the server — this browser keeps both ids.
  saveSession({
    trackingId: data.trackingId,
    sessionId: data.sessionId,
    applicant: { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() },
  });
  return data.veriffUrl;
}

// Asks the backend, which asks Veriff, for the current decision.
export async function getStatus({ trackingId, sessionId }) {
  const params = new URLSearchParams({ trackingId, sessionId });
  const res = await fetch(`${API_URL}/idv/verification/status?${params}`);
  if (!res.ok) {
    const err = new Error("Could not check verification status.");
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Asks the backend to build the PDF report and email it. Fire-and-forget.
// Sends at most once per verification, even if the result page is reloaded.
export async function emailReport({ trackingId, sessionId }) {
  const flag = `${STORAGE_KEY}.report.${trackingId}`;
  try {
    if (localStorage.getItem(flag)) return { sent: false, alreadySent: true };
    localStorage.setItem(flag, "1");
  } catch {
    /* storage blocked — a reload may send a second copy */
  }

  let applicant = {};
  try {
    applicant = JSON.parse(localStorage.getItem(STORAGE_KEY))?.applicant || {};
  } catch {
    applicant = {};
  }
  const res = await fetch(`${API_URL}/idv/verification/report`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ trackingId, sessionId, ...applicant }),
  });
  if (!res.ok) {
    try {
      localStorage.removeItem(flag);   // let a later attempt retry
    } catch {
      /* ignore */
    }
    throw new Error(SERVICE_ERROR);
  }
  return res.json();
}

// { trackingId, sessionId? } from localStorage. trackingId also arrives on
// Veriff's callback URL; sessionId only exists in the browser that started
// the verification (so it's missing on a phone Veriff handed off to).
export function getSession() {
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    stored = {};
  }
  const fromUrl = new URLSearchParams(window.location.search).get("trackingId");
  const trackingId = fromUrl || stored.trackingId;
  if (!trackingId) return null;

  // Only pair the stored sessionId with the tracking id it was created for
  const sessionId = stored.trackingId === trackingId ? stored.sessionId : undefined;
  if (fromUrl && fromUrl !== stored.trackingId) saveSession({ trackingId });

  return { trackingId, sessionId };
}

function saveSession(session) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* storage blocked */
  }
}

export function clearSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}